import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type DocumentReference,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  connectionRequestSchema,
  type ConnectionRequest,
  type ConnectionRequestType,
} from "./schemas";

export type ConnectionRequestFields =
  | Record<string, never>
  | { code: string; eventId: string }
  | { otherUid: string };

/**
 * Files a request on connection_requests and returns its reference at once.
 *
 * Deliberately not awaited here. With the persistent cache a write made
 * offline is kept and sent when the device is back online, and `written`
 * settles only then; until it does, the page says "Will connect when you're
 * back online". The Connections page finds such writes again after a reload
 * through the pending query in use-connections.ts.
 */
export function fileConnectionRequest(
  uid: string,
  type: ConnectionRequestType,
  fields: ConnectionRequestFields = {},
): { ref: DocumentReference; written: Promise<void> } {
  const ref = doc(collection(db, "connection_requests"));
  const written = setDoc(ref, {
    type,
    requestedBy: uid,
    requestedAt: serverTimestamp(),
    status: "REQUESTED",
    ...fields,
  });
  return { ref, written };
}

export type ConnectionRequestState =
  | { status: "idle" }
  /** Written on this device, not yet at the server. */
  | { status: "queued" }
  /** At the server; the trigger has not answered yet. */
  | { status: "waiting" }
  | { status: "succeeded"; request: ConnectionRequest }
  | { status: "failed"; error: string };

/** The state a request document's snapshot stands for. */
export function requestState(data: unknown): ConnectionRequestState | null {
  const parsed = connectionRequestSchema.safeParse(data);
  if (!parsed.success) return null;
  if (parsed.data.status === "SUCCEEDED") {
    return { status: "succeeded", request: parsed.data };
  }
  if (parsed.data.status === "FAILED") {
    return { status: "failed", error: parsed.data.error ?? "Request failed" };
  }
  return { status: "waiting" };
}

/**
 * Watches one filed request until the trigger settles it, then stops. The
 * caller starts it once `written` has resolved, for the reason given on
 * useConnectionRequest below. A listener the rules refuse counts as failed.
 */
export function watchConnectionRequest(
  ref: DocumentReference,
  onSettled: (
    state: Extract<
      ConnectionRequestState,
      { status: "succeeded" } | { status: "failed" }
    >,
  ) => void,
): () => void {
  let done = false;
  const stop = onSnapshot(
    ref,
    (snapshot) => {
      if (done || !snapshot.exists()) return;
      const next = requestState(snapshot.data());
      if (next?.status === "succeeded" || next?.status === "failed") {
        done = true;
        stop();
        onSettled(next);
      }
    },
    (error) => {
      if (done) return;
      done = true;
      onSettled({ status: "failed", error: error.message });
    },
  );
  return () => {
    done = true;
    stop();
  };
}

/**
 * One request at a time, filed and watched until the trigger settles it.
 *
 * The listener starts once the write has reached the server. The read rule
 * compares the stored requester, so a listener on a document the server does
 * not have yet is refused and torn down; until the write lands, the state is
 * "queued", which offline lasts until the device is back.
 */
export function useConnectionRequest(uid: string) {
  const [state, setState] = useState<ConnectionRequestState>({
    status: "idle",
  });
  const unsubscribe = useRef<(() => void) | null>(null);
  // Which submit the callbacks belong to, so a late answer to a request the
  // person has moved on from changes nothing.
  const generation = useRef(0);

  useEffect(
    () => () => {
      generation.current += 1;
      unsubscribe.current?.();
    },
    [],
  );

  const submit = useCallback(
    (type: ConnectionRequestType, fields: ConnectionRequestFields = {}) => {
      unsubscribe.current?.();
      unsubscribe.current = null;
      const mine = ++generation.current;
      const { ref, written } = fileConnectionRequest(uid, type, fields);
      setState({ status: "queued" });
      written.then(
        () => {
          if (generation.current !== mine) return;
          setState({ status: "waiting" });
          const stop = onSnapshot(
            ref,
            (snapshot) => {
              if (generation.current !== mine || !snapshot.exists()) return;
              const next = requestState(snapshot.data());
              if (!next) return;
              setState(next);
              if (next.status === "succeeded" || next.status === "failed") {
                stop();
                unsubscribe.current = null;
              }
            },
            (error) => {
              if (generation.current !== mine) return;
              setState({ status: "failed", error: error.message });
            },
          );
          unsubscribe.current = stop;
        },
        (error: unknown) => {
          if (generation.current !== mine) return;
          setState({
            status: "failed",
            error:
              error instanceof Error
                ? error.message
                : "Could not send the request",
          });
        },
      );
    },
    [uid],
  );

  const reset = useCallback(() => {
    generation.current += 1;
    unsubscribe.current?.();
    unsubscribe.current = null;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
